import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { fr, enUS, zhCN, arSA } from 'date-fns/locale';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PublicIcon from '@mui/icons-material/Public';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Check';
import { toastr } from 'react-redux-toastr';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
  setDestination,
  setEndDate,
  setStartDate,
} from '../santeMobiliteSlice';
import { Country } from '../types';
import { listCountriesApi } from '@/api/santeMobiliteApi';
import { formatDateOnly } from '@/utils/date';
import { startOfTomorrow } from '../utils';
import { destinationLabel } from '../countryLabel';

const parseIsoToDate = (iso: string | null): Date | null => {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
};

const toUtcMidnightIsoFromDate = (d: Date): string => {
  return new Date(
    Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0),
  ).toISOString();
};

const formatDate = (iso: string | null) => {
  if (!iso) return 'XX/XX/XXXX';
  return formatDateOnly(iso);
};

const DATE_LOCALES: Record<string, Locale> = {
  fr,
  en: enUS,
  zh: zhCN,
  ar: arSA,
};

/**
 * Cap métier "1 an de couverture" : end max = anniversaire de start - 1 jour.
 * Doit rester EXACTEMENT cohérent avec ContractDatesStep et la formule
 * monthsCount du back (cf. utils.ts). Si on autorisait l'anniversaire on
 * aurait 366 jours de couverture → 13 mois.
 */
const maxEndDateForOneYear = (start: Date): Date => {
  const r = new Date(start);
  r.setFullYear(r.getFullYear() + 1);
  r.setDate(r.getDate() - 1);
  return r;
};

const addDays = (d: Date, days: number) => {
  const r = new Date(d);
  r.setDate(r.getDate() + days);
  return r;
};

/** Style "input arrondi" cohérent avec le reste du parcours. */
const ROUND_INPUT_SX: SxProps<Theme> = {
  '& .MuiInputBase-root': { borderRadius: '30px' },
};

const EditableRecapCard: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation('sante-mobilite');
  const { startDate, endDate, destination } = useAppSelector(
    state => state.santeMobilite,
  );

  const dateLocale =
    DATE_LOCALES[i18n.language?.split('-')[0] || 'fr'] || fr;

  const [editing, setEditing] = useState(false);
  const [localStart, setLocalStart] = useState<Date | null>(parseIsoToDate(startDate));
  const [localEnd, setLocalEnd] = useState<Date | null>(parseIsoToDate(endDate));
  const [localDestination, setLocalDestination] = useState<Country | null>(destination);
  const [countries, setCountries] = useState<Country[]>([]);
  const [countriesLoading, setCountriesLoading] = useState(false);

  // Sync local state si Redux change pendant qu'on n'est pas en édition.
  useEffect(() => {
    if (!editing) {
      setLocalStart(parseIsoToDate(startDate));
      setLocalEnd(parseIsoToDate(endDate));
      setLocalDestination(destination);
    }
  }, [startDate, endDate, destination, editing]);

  // Charge la liste des pays au moment où l'utilisateur entre en édition (paresseux).
  useEffect(() => {
    if (!editing || countries.length > 0) return;
    let cancelled = false;
    setCountriesLoading(true);
    listCountriesApi()
      .then(res => {
        const data: Country[] = res.data?.data ?? res.data ?? [];
        if (!cancelled) setCountries(data);
      })
      .catch(() => {
        if (!cancelled) setCountries([]);
      })
      .finally(() => {
        if (!cancelled) setCountriesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [editing, countries.length]);

  // Libellés traduits, et tri sur le libellé traduit : une liste ordonnée
  // alphabétiquement en français n'a plus de sens affichée en chinois.
  const countryLabel = (c: Country | null | undefined) =>
    destinationLabel(c?.name, i18n.language);

  const sortedCountries = useMemo(
    () =>
      [...countries].sort((a, b) =>
        countryLabel(a).localeCompare(countryLabel(b), i18n.language),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [countries, i18n.language],
  );

  const handleSave = () => {
    if (!localStart || !localEnd) {
      toastr.warning(
        t('step1.toastr_invalid_title'),
        t('step5.recap_missing_dates'),
      );
      return;
    }
    if (localStart < startOfTomorrow()) {
      toastr.warning(
        t('step1.toastr_invalid_title'),
        t('step1.toastr_start_past'),
      );
      return;
    }
    if (localEnd <= localStart) {
      toastr.warning(
        t('step1.toastr_invalid_title'),
        t('step1.toastr_end_before_start'),
      );
      return;
    }
    // Cap métier 1 an : on rejette si end > maxEndDateForOneYear(start).
    if (localEnd > maxEndDateForOneYear(localStart)) {
      toastr.warning(
        t('step1.toastr_invalid_title'),
        t('step1.toastr_max_one_year'),
      );
      return;
    }
    if (!localDestination) {
      toastr.warning(
        t('step1.toastr_invalid_title'),
        t('step5.recap_missing_destination'),
      );
      return;
    }

    const now = new Date();
    const isToday =
      localStart.getFullYear() === now.getFullYear() &&
      localStart.getMonth() === now.getMonth() &&
      localStart.getDate() === now.getDate();
    const startISO = isToday
      ? now.toISOString()
      : toUtcMidnightIsoFromDate(localStart);

    dispatch(setStartDate(startISO));
    dispatch(setEndDate(toUtcMidnightIsoFromDate(localEnd)));
    dispatch(setDestination(localDestination));
    // L'invalidation du prix est gérée automatiquement par les reducers
    // setStartDate / setEndDate / setDestination quand la valeur change.
    setEditing(false);
  };

  return (
    <Box
      sx={{
        position: 'relative',
        p: 3,
        borderRadius: 3,
        backgroundColor: 'rgba(47, 105, 117, 0.15)',
        color: '#2f3e46',
      }}
    >
      <Typography
        variant="h6"
        fontWeight={600}
        sx={{ textTransform: 'uppercase', mb: 2 }}
      >
        {t('step5.recap_title')}
      </Typography>

      <IconButton
        size="small"
        sx={{ position: 'absolute', top: 8, right: 8, color: '#2f6975' }}
        onClick={editing ? handleSave : () => setEditing(true)}
        aria-label={editing ? 'Enregistrer' : 'Modifier'}
      >
        {editing ? <SaveIcon /> : <EditIcon />}
      </IconButton>

      {/* Date de départ */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <CalendarTodayIcon sx={{ color: '#2f6975', fontSize: 18 }} />
        {editing ? (
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateLocale}>
            <DatePicker
              label={t('step5.start_date')}
              value={localStart}
              onChange={newDate => {
                setLocalStart(newDate);
                // Si l'end actuelle est antérieure ou égale à la nouvelle
                // start, on la reset pour forcer une nouvelle saisie.
                if (newDate && localEnd && localEnd <= newDate) {
                  setLocalEnd(null);
                }
              }}
              // `minDate` et non `disablePast` : ce dernier n'exclut que les
              // jours révolus et laissait donc rééditer la date de départ sur
              // aujourd'hui, contournant la règle posée à l'étape 1.
              minDate={startOfTomorrow()}
              format="dd/MM/yyyy"
              slotProps={{
                textField: {
                  size: 'small',
                  InputLabelProps: { shrink: true },
                  sx: ROUND_INPUT_SX,
                },
              }}
            />
          </LocalizationProvider>
        ) : (
          <Typography variant="body2" sx={{ textTransform: 'uppercase' }}>
            <strong>{t('step5.start_date')} :</strong> {formatDate(startDate)}
          </Typography>
        )}
      </Box>

      {/* Date de fin */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <CalendarTodayIcon sx={{ color: '#2f6975', fontSize: 18 }} />
        {editing ? (
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateLocale}>
            <DatePicker
              label={t('step5.end_date')}
              value={localEnd}
              onChange={newDate => setLocalEnd(newDate)}
              disabled={!localStart}
              format="dd/MM/yyyy"
              minDate={localStart ? addDays(localStart, 1) : undefined}
              maxDate={localStart ? maxEndDateForOneYear(localStart) : undefined}
              slotProps={{
                textField: {
                  size: 'small',
                  InputLabelProps: { shrink: true },
                  sx: ROUND_INPUT_SX,
                },
              }}
            />
          </LocalizationProvider>
        ) : (
          <Typography variant="body2" sx={{ textTransform: 'uppercase' }}>
            <strong>{t('step5.end_date')} :</strong> {formatDate(endDate)}
          </Typography>
        )}
      </Box>

      {/* Destination */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PublicIcon sx={{ color: '#2f6975', fontSize: 18 }} />
        {editing ? (
          <Autocomplete
            sx={{ minWidth: 220 }}
            size="small"
            options={sortedCountries}
            loading={countriesLoading}
            getOptionLabel={o => countryLabel(o)}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            value={localDestination}
            onChange={(_, value) => setLocalDestination(value)}
            renderInput={params => (
              <TextField
                {...params}
                label={t('step5.destination')}
              />
            )}
          />
        ) : (
          <Typography variant="body2" sx={{ textTransform: 'uppercase' }}>
            <strong>{t('step5.destination')} :</strong>{' '}
            {destinationLabel(destination?.name, i18n.language)}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default EditableRecapCard;
